import { BackToHomeButton } from "@/components/booking/BackToHomeButton";
import { DesktopAppFrame } from "@/components/desktop/DesktopChrome";
import { ThemedText } from "@/components/themed-text";
import { Avatar } from "@/components/ui/avatar";
import { BrandColors, Colors } from "@/constants/theme";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import {
  invalidateMessageQueries,
  removeOptimisticMessage,
  useAppendChatMessage,
  useChatMessages,
} from "@/hooks/queries/use-messages-query";
import { showAlert } from "@/lib/alert";
import api, { getApiErrorMessage } from "@/lib/api";
import { isCustomerRole } from "@/lib/roles";
import messageService from "@/lib/services/messages";
import { webSocketService } from "@/lib/services/websocket";
import { showToast } from "@/lib/toast";
import { User } from "@/lib/types/user";
import { useAuthStore } from "@/stores/use-auth-store";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  Stack,
  router,
  useFocusEffect,
  useLocalSearchParams,
} from "expo-router";
import React, { useCallback, useEffect, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Modal,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useColorScheme,
  useWindowDimensions,
} from "react-native";
import {
  Bubble,
  Composer,
  GiftedChat,
  IMessage,
  InputToolbar,
  MessageTextProps,
  Send,
} from "react-native-gifted-chat";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";

function isCompletedBookingError(error: unknown): boolean {
  return (
    error instanceof AxiosError &&
    error.response?.status === 403 &&
    error.response?.data?.error === "completed_booking"
  );
}

export default function ChatRoute() {
  return (
    <DesktopAppFrame>
      <ChatScreen />
    </DesktopAppFrame>
  );
}

function ChatScreen() {
  const desktop = useDesktopLayout();
  const { id, user: receiverParam, reload = "false" } = useLocalSearchParams();
  const qc = useQueryClient();
  const bookingId = Number(id);
  const colorScheme = useColorScheme();
  const [sendError, setSendError] = useState<unknown>(null);
  const theme = Colors[colorScheme ?? "light"];
  const { width } = useWindowDimensions();
  const MAX_BUBBLE_WIDTH = width >= 1024 ? 1024 * 0.75 : width * 0.75;

  const receiver: User =
    typeof receiverParam === "string" ? JSON.parse(receiverParam) : null;

  const { user, setActiveBooking } = useAuthStore();
  const insets = useSafeAreaInsets();
  const tabbarHeight = 80;
  const keyboardTopToolbarHeight = Platform.select({ ios: 44, default: 0 });
  const keyboardVerticalOffset =
    insets.bottom + tabbarHeight + keyboardTopToolbarHeight;

  // Load message history once (staleTime: Infinity — WS keeps it live)
  const {
    data: chatMessages = [],
    error,
    refetch,
    isRefetching,
  } = useChatMessages(bookingId);
  const appendMessage = useAppendChatMessage(bookingId);

  const displayError = error ?? sendError;
  const isCompletedBooking = isCompletedBookingError(displayError);
  const errorMessage = displayError
    ? getApiErrorMessage(
        displayError,
        "Unable to load messages. Please try again.",
      )
    : null;

  useFocusEffect(
    useCallback(() => {
      setActiveBooking(bookingId);
      if (reload === "true") {
        invalidateMessageQueries(qc, bookingId);
      }
    }, [qc, reload, bookingId, setActiveBooking]),
  );

  // Socket-ID header interceptor for broadcasting control
  useEffect(() => {
    const { echoRef } = webSocketService;
    if (!echoRef) return;

    const interceptorId = api.interceptors.request.use((config) => {
      const socketId = echoRef.socketId();
      if (socketId) {
        config.headers = config.headers ?? {};
        config.headers["X-Socket-ID"] = socketId;
      }
      return config;
    });

    return () => {
      api.interceptors.request.eject(interceptorId);
    };
  }, []);

  const onSend = async (newMessages: IMessage[] = []) => {
    const msg = newMessages[0];
    if (!msg || !user || !receiver) return;

    const data = {
      booking_id: bookingId,
      receiver_id: Number(receiver.id),
      message: msg.text,
    };

    // Optimistic: append immediately into the query cache
    appendMessage(newMessages);

    try {
      await messageService.sendMessage(data);
    } catch (err) {
      if (isCompletedBookingError(err)) {
        setSendError(err);
        return;
      }
      removeOptimisticMessage(qc, msg, bookingId);

      showToast.error("Failed to send message. Please check your connection.");
    }
  };

  const renderBubble = (props: any) => {
    return (
      <Bubble
        {...props}
        // Pressable steals long-press; disable so Text selectable works
        touchableProps={{ disabled: true }}
        wrapperStyle={{
          right: {
            backgroundColor: BrandColors.primary,
            maxWidth: MAX_BUBBLE_WIDTH,
          },
          left: {
            backgroundColor: "#fff",
            maxWidth: MAX_BUBBLE_WIDTH,
          },
        }}
        textStyle={{
          right: {
            color: "#fff",
          },
          left: {
            color: BrandColors.charcoal,
          },
        }}
      />
    );
  };

  const renderSend = (props: any) => {
    return (
      <Send {...props}>
        <View style={styles.sendButton}>
          <Ionicons name="send" size={24} color={BrandColors.primary} />
        </View>
      </Send>
    );
  };

  const renderInputToolbar = (props: any) => {
    return (
      <InputToolbar
        {...props}
        containerStyle={[
          styles.inputToolbar,
          { backgroundColor: theme.background, borderColor: theme.background },
        ]}
        primaryStyle={{ alignItems: "center" }}
      />
    );
  };

  const renderComposer = (props: any) => {
    return (
      <Composer
        {...props}
        textInputStyle={styles.composer}
        placeholderTextColor={BrandColors.mediumGray}
      />
    );
  };

  const handleCall = () => {
    if (receiver?.phone) {
      Linking.openURL(`tel:${receiver.phone}`);
    } else {
      showAlert("Error", "Unable to make call. Please try again later");
    }
  };

  const renderMessageText = ({
    currentMessage,
    position = "left",
    textStyle,
  }: MessageTextProps<IMessage>) => {
    return (
      <View style={styles.messageTextContainer}>
        <Text selectable style={[styles.messageText, textStyle?.[position]]}>
          {currentMessage?.text}
        </Text>
      </View>
    );
  };

  const renderHeaderTitle = () => (
    <View style={styles.headerTitleContainer}>
      <Avatar
        src={receiver?.avatar}
        name={receiver?.name || "Unknown"}
        size={30}
      />
      <View style={styles.headerText}>
        <ThemedText type="defaultSemiBold" style={styles.headerName}>
          {receiver?.name}
        </ThemedText>
        <ThemedText type="small" style={styles.headerStatus}>
          online
        </ThemedText>
      </View>
    </View>
  );

  const renderHeaderRight = () => (
    <TouchableOpacity onPress={handleCall} style={styles.headerButton}>
      <Ionicons name="call" size={28} color={BrandColors.primary} />
    </TouchableOpacity>
  );

  const renderHeaderLeft = () => (
    <TouchableOpacity
      onPress={() => {
        if (router.canGoBack()) {
          router.back();
        } else {
          router.replace(
            isCustomerRole(user?.role)
              ? `/bookings/${bookingId}/live-tracking`
              : `/bookings/${bookingId}/live-driving`,
          );
        }
      }}
      style={styles.headerButton}
    >
      <Ionicons name="chevron-back" size={30} color={theme.text} />
    </TouchableOpacity>
  );

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
      edges={["bottom"]}
    >
      <Stack.Screen
        options={{
          headerTitle: renderHeaderTitle,
          headerRight: renderHeaderRight,
          headerLeft: renderHeaderLeft,
          headerTitleAlign: "center",
          headerShown: !desktop,
          headerStyle: {
            backgroundColor: theme.background,
          },
          headerTintColor: theme.text,
        }}
      />
      {desktop && (
        <View style={[styles.desktopHeader, { borderBottomColor: theme.border }]}>
          {renderHeaderLeft()}
          {renderHeaderTitle()}
          {renderHeaderRight()}
        </View>
      )}
      <GiftedChat
        messages={chatMessages}
        onSend={(messages) => onSend(messages)}
        user={{
          _id: String(user?.id),
          name: user?.first_name,
          avatar: user?.avatar,
        }}
        renderBubble={renderBubble}
        renderSend={renderSend}
        renderMessageText={renderMessageText}
        renderInputToolbar={renderInputToolbar}
        textInputProps={{
          style: [
            styles.composer,
            {
              backgroundColor: theme.background,
              borderColor: theme.background,
            },
          ],
          placeholderTextColor: BrandColors.mediumGray,
        }}
        keyboardAvoidingViewProps={{ keyboardVerticalOffset }}
        listProps={{
          showsVerticalScrollIndicator: false,
        }}
      />

      <Modal
        visible={Boolean(displayError)}
        transparent
        animationType="fade"
        onRequestClose={() => {
          if (!isCompletedBooking) {
            setSendError(null);
            refetch();
          }
        }}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[styles.modalContent, { backgroundColor: theme.background }]}
          >
            <View style={styles.modalHeader}>
              <Ionicons
                name="alert-circle"
                size={40}
                color={BrandColors.error}
              />
              <ThemedText style={styles.modalTitle} type="subtitle">
                {isCompletedBooking
                  ? "Chat unavailable"
                  : "Unable to load chat"}
              </ThemedText>
            </View>

            <ThemedText style={styles.modalDescription}>
              {errorMessage}
            </ThemedText>

            {isCompletedBooking ? (
              <BackToHomeButton />
            ) : (
              <TouchableOpacity
                style={styles.refreshButton}
                onPress={() => {
                  setSendError(null);
                  refetch();
                }}
                disabled={isRefetching}
              >
                {isRefetching ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <ThemedText style={styles.refreshButtonText}>
                    Refresh
                  </ThemedText>
                )}
              </TouchableOpacity>
            )}
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  desktopHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  messageTextContainer: {
    marginVertical: 5,
    marginHorizontal: 10,
    flexShrink: 1,
  },
  messageText: {
    fontSize: 16,
    lineHeight: 20,
    flexShrink: 1,
  },
  sendButton: {
    marginBottom: 10,
    marginRight: 10,
  },
  inputToolbar: {
    borderTopColor: BrandColors.lightGray,
    padding: 4,
  },
  composer: {
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingTop: 8,
    paddingBottom: 8,
    marginRight: 10,
    marginHorizontal: 10,
    fontFamily: "PlusJakartaSans",
  },
  headerTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  headerText: {
    marginLeft: 10,
  },
  headerName: {
    fontSize: 16,
  },
  headerButton: {
    backgroundColor: "transparent",
    padding: 0,
    margin: 0,
  },
  headerStatus: {
    fontSize: 12,
    color: BrandColors.primary,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    padding: 20,
  },
  modalContent: {
    borderRadius: 20,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  modalHeader: {
    alignItems: "center",
    marginBottom: 16,
  },
  modalTitle: {
    marginTop: 8,
    textAlign: "center",
  },
  modalDescription: {
    textAlign: "center",
    marginBottom: 24,
    color: BrandColors.mediumGray,
    lineHeight: 22,
  },
  refreshButton: {
    borderRadius: 12,
    padding: 16,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BrandColors.primary,
    minHeight: 52,
  },
  refreshButtonText: {
    fontSize: 16,
    color: "#fff",
    fontWeight: "600",
  },
});
